import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { useGetWebsiteGitInfoQuery } from 'src/api_services/websites/query';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { IconStack } from 'src/components/IconStack/IconStack';
import { Text } from 'src/components/Text';
import { Viewport } from 'src/containers/Viewport';
import { isWebsiteNormalOrStaging } from 'src/utils/isWebsiteNormal';
import { LoginMembership, Website } from 'src/orchd-client';

export interface Props {
  website: Website;
  member: LoginMembership;
}

export const _GithubConnectionStatus = ({ website, member }: Props) => {
  const { orgId } = member;

  const isGitWebsite = isWebsiteNormalOrStaging(website.kind);

  const { data: githubInfo, isLoading } = useGetWebsiteGitInfoQuery({
    orgId,
    websiteId: website.id,
    // git is only meaningful for normal/staging websites; skip the
    // lookup (and any UI) for service or server hostname websites
    enabled: isGitWebsite,
  });

  // 404 (no connection) is an expected response; render nothing while the
  // first fetch is in flight so the button/connected states don't flash
  if (!isGitWebsite || isLoading) {
    return null;
  }

  const isConnected = !!githubInfo?.branchOrTagName;
  const gitUrl = `/websites/${website.id}/git`;

  return (
    <Box d="row" a="center" gap="sm" w="auto" wrap="nowrap">
      {isConnected ? (
        <>
          <Link to={gitUrl}>
            <IconStack icons={['github']} borderColor="green" />
          </Link>
          <Viewport tabletUp>
            <Text size="sm">
              <FormattedMessage
                id="websites.website_dashboard.hero.github_status.connected"
                defaultMessage="Connected"
              />
            </Text>
          </Viewport>
        </>
      ) : (
        <>
          <Link to={gitUrl}>
            <IconStack icons={['github']} iconColor="grey" />
          </Link>
          <Viewport tabletUp>
            <Link to={gitUrl}>
              <Button variant="tertiary" size="small">
                <FormattedMessage
                  id="websites.website_dashboard.hero.github_status.configure_repository"
                  defaultMessage="Configure {git}"
                  values={{ git: 'GitHub' }}
                />
              </Button>
            </Link>
          </Viewport>
        </>
      )}
    </Box>
  );
};

export const GithubConnectionStatus = memo(_GithubConnectionStatus);
